import React from "react";
import { Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { MetricInsight } from "@/utils/parseBlueprintSummary";

export interface SubMetricRow {
  label: string;
  score: number | null;
  /** Overrides default percentage formatting (e.g. originality "not applicable"). */
  displayScore?: string;
  insight?: MetricInsight;
}

interface ScorecardCriterionCardProps {
  criterionNumber: number;
  title: string;
  score: number | null;
  expanded: boolean;
  onToggle: () => void;
  pillarSummary?: string;
  subMetrics: SubMetricRow[];
  /** When false, breakdown toggle is visual-only (landing preview). */
  interactive?: boolean;
  compact?: boolean;
}

function scoreOnTen(pct: number | null): string {
  if (pct == null || Number.isNaN(pct)) return "—";
  return String(Math.round(pct / 10)).padStart(2, "0");
}

function fmtPct(pct: number | null): string {
  if (pct == null || Number.isNaN(pct)) return "—";
  const r = Math.round(pct * 10) / 10;
  return `${r.toFixed(1)}%`;
}

export function ScorecardCriterionCard({
  criterionNumber,
  title,
  score,
  expanded,
  onToggle,
  pillarSummary,
  subMetrics,
  interactive = true,
  compact = false,
}: ScorecardCriterionCardProps) {
  const progress = score != null ? Math.min(100, Math.max(0, score)) : 0;
  const hasBreakdown = subMetrics.length > 0;

  return (
    <div
      className={cn(
        "rounded-lg border bg-[#141414] transition-colors duration-200",
        expanded ? "border-gold-button" : "border-[#2a2a2a] hover:border-[#3a3a3a]"
      )}
    >
      <div className={compact ? "p-3 md:p-3.5" : "p-5 md:p-6"}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p
              className={
                compact
                  ? "text-[9px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-1"
                  : "text-[10px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-2"
              }
            >
              Criterion {String(criterionNumber).padStart(2, "0")}
            </p>
            <h3
              className={
                compact
                  ? "text-sm md:text-base font-sans font-semibold text-white leading-tight"
                  : "text-lg md:text-xl font-sans font-semibold text-white leading-tight"
              }
            >
              {title}
            </h3>
          </div>
          <div className="text-right shrink-0">
            <span
              className={
                compact
                  ? "text-xl md:text-2xl font-sans font-bold text-gold-button leading-none"
                  : "text-3xl md:text-4xl font-sans font-bold text-gold-button leading-none"
              }
            >
              {scoreOnTen(score)}
            </span>
            <p className={compact ? "text-[10px] font-sans text-white/40 mt-0.5" : "text-xs font-sans text-white/40 mt-1"}>
              / 10
            </p>
          </div>
        </div>

        {/* Progress track */}
        <div className={compact ? "mt-3 relative h-[2px] bg-white/10 rounded-full" : "mt-5 relative h-[2px] bg-white/10 rounded-full"}>
          <div
            className="absolute left-0 top-0 h-full bg-gold-button rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-gold-button border-2 border-[#141414] transition-all duration-500"
            style={{ left: `calc(${progress}% - 6px)` }}
          />
        </div>

        {expanded && (
          <div className="mt-6 pt-5 border-t border-white/10 animate-fade-in">
            {pillarSummary && (
              <p className="text-sm font-sans text-white/80 leading-relaxed mb-6">{pillarSummary}</p>
            )}

            <p className="text-[10px] font-sans font-medium uppercase tracking-[0.2em] text-white/40 mb-4">
              Derivation
            </p>

            <div className="space-y-5">
              {subMetrics.map((sub) => (
                <div key={sub.label} className="space-y-2">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-sans font-medium text-white">{sub.label}</span>
                    <span className="text-sm font-sans text-white/50 shrink-0">
                      {sub.displayScore ?? fmtPct(sub.score)}
                    </span>
                  </div>
                  {sub.insight ? (
                    <div className="space-y-3 pl-0 text-sm font-sans text-white/60 leading-relaxed">
                      {sub.insight.whatWorks && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1">What works</p>
                          <p>{sub.insight.whatWorks}</p>
                        </div>
                      )}
                      {sub.insight.whatDoesNot && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1">What does not</p>
                          <p>{sub.insight.whatDoesNot}</p>
                        </div>
                      )}
                      {sub.insight.actionableNotes && (
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-white/35 mb-1">Actionable notes</p>
                          <p>{sub.insight.actionableNotes}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm font-sans text-white/40 italic">No detailed insights for this sub-metric.</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={interactive ? onToggle : undefined}
          disabled={!interactive || (!hasBreakdown && !expanded)}
          className={cn(
            compact
              ? "mt-3 w-full flex items-center justify-between rounded-full px-3 py-2"
              : "mt-5 w-full flex items-center justify-between rounded-full px-4 py-2.5",
            "text-[10px] font-sans font-medium uppercase tracking-[0.15em] transition-colors",
            "bg-gold-button text-neutral-700",
            interactive && (hasBreakdown || expanded)
              ? "hover:bg-gold-light cursor-pointer"
              : "cursor-default"
          )}
          aria-expanded={expanded}
        >
          <span>{expanded ? "Hide breakdown" : "View breakdown"}</span>
          <span
            className={cn(
              "flex items-center justify-center w-6 h-6 rounded-full",
              "border border-neutral-600/30 text-neutral-700"
            )}
          >
            {expanded ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
          </span>
        </button>
      </div>
    </div>
  );
}
